import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {CatalogSkuDetail} from '../../models/catalog/stock-keeping-unit.model';
import {CatalogCategoryInfo} from '../../models/catalog/category.model';
import {TradePolicyModel} from '../../models/catalog/trade-policy.model';

export type CatalogOrder = 'ASC' | 'DESC';

export interface CatalogSearchRequest {
  // exactly one of these should be provided
  productId?: number;
  refId?: string;
  skuId?: number;
  productName?: string;

  size?: number; // page size
  order?: CatalogOrder; // default DESC
  orderBy?: 'Date'; // always Date per requirements

  // cursor pagination
  after?: number; // id of last item of current page
  afterDate?: string; // lastUpdateDate of last item
  before?: number; // id of first item of current page
  beforeDate?: string; // lastUpdateDate of first item
}

export interface CatalogProductItem {
  id: number;
  name: string;
  refId: string;
  linkId: string;
  mainImageUrl: string;
  totalSku: number;
  lastUpdateDate: string; // ISO string
  creationDate: string; // ISO string
  isActive: boolean;
  isMarketplace: boolean;
  skuOffers: CatalogSearchOffersSkus[];
}

export interface CatalogSearchOffersSkus {
  id: string;
  lastTagUpdateDate: string;
  sellers: CatalogSearchOfferSellers[];
}

export interface CatalogOfferModel {
  linkText: string;
  productId: string;
  skus: CatalogSearchOffersSkus[];
}

export interface CatalogSearchOfferSellers {
  id: string;
  name: string;
  saleChannel?: string[] | string;
  price: number;
  inventory: number;
}

export interface CatalogSearchResponse {
  total: number;
  data: CatalogProductItem[];
}

export interface CatalogProductSpecification {
  mainFieldName: string;
  fieldValueName: string;
  productFieldValueText: string;
}

export interface CatalogProductSalesChannel {
  storeId: number;
  name: string;
  currencySymbol: string;
  cultureInfo: string;
  isActive: boolean;
  countryCode: string;
}

export interface CatalogProductDetail {
  productId: number;
  name: string;
  title?: string;
  refId?: string;
  linkId: string;
  brandId?: number;
  brandName?: string;
  categoryId?: number;
  categoryName?: string;
  description?: string;
  lastUpdateDate?: string;
  releaseDate?: string;
  isActive?: boolean;
  isVisible?: boolean;
  isMarketplace: boolean;
  marketplaceStore?: string;
  specifications: CatalogProductSpecification[];
  skus: CatalogSkuDetail[];
  stores: CatalogProductSalesChannel[];
  category: CatalogCategoryInfo;
  showWithoutStock: boolean;
}

export interface CatalogPriceModel {
  itemId: string;
  listPrice?: number;
  costPrice?: number;
  markup?: number;
  basePrice?: number;
  fixedPrices?: CatalogPriceFixedPriceModel[];
}

export interface CatalogPriceFixedPriceModel {
  tradePolicyId: string;
  value?: number;
  listPrice?: number;
  minQuantity?: number;
  dateRange?: CatalogPriceDateRangeModel;
}

export interface CatalogPriceDateRangeModel {
  from: string;
  to: string;
}

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly baseUrl = `${environment.backend.url}/api/vtex/catalog/v1`;

  constructor(private readonly http: HttpClient) {}

  search(req: CatalogSearchRequest): Observable<CatalogSearchResponse> {
    let params = new HttpParams();

    if (req.productId != null) params = params.set('productId', String(req.productId));
    if (req.refId != null && req.refId !== '') params = params.set('refId', req.refId);
    if (req.skuId != null) params = params.set('skuId', String(req.skuId));
    if (req.productName != null && req.productName !== '') params = params.set('productName', req.productName);

    if (req.size != null) params = params.set('size', String(req.size));
    params = params.set('order', (req.order ?? 'DESC'));
    params = params.set('orderBy', 'Date');

    if (req.after != null) params = params.set('after', String(req.after));
    if (req.afterDate != null) params = params.set('afterDate', req.afterDate);
    if (req.before != null) params = params.set('before', String(req.before));
    if (req.beforeDate != null) params = params.set('beforeDate', req.beforeDate);

    return this.http.get<CatalogSearchResponse>(`${this.baseUrl}/search`, { params });
  }

  find(productId: number): Observable<CatalogProductDetail> {
    return this.http.get<CatalogProductDetail>(`${this.baseUrl}/find/${productId}`);
  }

  getPrices(skuId: number): Observable<CatalogPriceModel> {
    return this.http.get<CatalogPriceModel>(`${this.baseUrl}/prices/${skuId}`);
  }

  getOffers(productId: number): Observable<CatalogOfferModel[]> {
    return this.http.get<CatalogOfferModel[]>(`${this.baseUrl}/offers/${productId}`);
  }

  getTradePolicies(): Observable<TradePolicyModel[]> {
    return this.http.get<TradePolicyModel[]>(`${this.baseUrl}/trade-policies`);
  }
}
